import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import tw from 'twin.macro';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { Container, ContentWithPaddingXl } from 'components/misc/Layouts';
import { SectionHeading } from 'components/misc/Headings';
import { useDispatch, useSelector } from 'react-redux';
import { getMensajeBloqueoMezclaCarrito, handleAddToCart } from 'utils/carritoUtils';
import { VIANDA_COUPON_MESSAGES } from 'utils/viandaCouponRules';
import { obtenerMenuActualViandas } from 'scripts/obtenerMenuActualViandas';

const Heading = tw(SectionHeading)``;
const Section = tw.section`mb-10`;
const Grid = tw.div`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6`;
const Card = tw.div`bg-white rounded-xl shadow-md p-6 border border-gray-100 flex flex-col`;
const Title = tw.h3`text-xl font-bold text-gray-900`;
const Meta = tw.p`text-sm text-gray-600 mt-1`;
const Price = tw.p`text-2xl font-bold text-green-700 mt-4`;
const Muted = tw.p`text-sm text-gray-600 mt-2`;
const Controls = tw.div`flex items-center gap-2 mt-4`;
const ControlButton = tw.button`px-3 py-1 bg-gray-200 rounded hover:bg-gray-300`;
const ChoiceButton = styled.button(({ $active }) => [
	tw`px-3 py-2 rounded border text-sm font-semibold transition`,
	$active ?
		tw`bg-[#99cdb6] text-white border-[#99cdb6]`
	:	tw`bg-white text-gray-700 border-gray-300 hover:border-gray-500`,
]);
const AddButton = tw.button`mt-6 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700`;
const MenuImage = tw.img`w-full rounded-xl shadow-md border border-gray-100`;
const FriendlyBox = tw.div`bg-[#f2faf6] border border-[#cbe9dc] rounded-xl p-5 text-gray-700`;
const CouponHint = styled.p(({ $blocked }) => [
	tw`text-sm mt-2 font-semibold`,
	$blocked ? tw`text-red-600` : tw`text-green-700`,
]);

const getNumber = (...values) => {
	for (const value of values) {
		const numberValue = Number(value);
		if (Number.isFinite(numberValue)) {
			return numberValue;
		}
	}
	return 0;
};

const resolveDiscount = (plan) => {
	if (!plan) {
		return null;
	}
	return plan.descuento || plan.descuento_activo || plan.tipo_descuento || null;
};

export default function Viandas() {
	const dispatch = useDispatch();
	const token = sessionStorage.getItem('token');
	const userInfo = useSelector((state) => state.usuario.userInfo);
	const viandasState = useSelector((state) => state.viandas || { items: [], status: 'idle' });
	const cantidadProductosCarrito = useSelector((state) => state.carrito.cantidadProductos);
	const cartItems = useSelector((state) => state.carrito.items || []);
	const [menu, setMenu] = useState(null);
	const [opciones, setOpciones] = useState({});
	const loading = viandasState.status === 'idle' || viandasState.status === 'loading';

	const puedeComprar =
		userInfo?.rol === 'Cliente' || userInfo?.rol === 'Administrador' || Number(userInfo?.rol_id) === 1;

	useEffect(() => {
		const loadData = async () => {
			try {
				const menuData = await obtenerMenuActualViandas();
				setMenu(menuData || null);
			} catch (error) {
				console.error(error);
				toast.error('No pudimos cargar las viandas en este momento.');
			}
		};

		loadData();
	}, []);

	const planes = useMemo(
		() => (Array.isArray(viandasState.items) ? viandasState.items : []).filter((item) => item?.activo !== false),
		[viandasState.items],
	);

	const planesNormalizados = useMemo(() => {
		return planes.map((plan) => {
			const descuento = resolveDiscount(plan);
			const precioPorVianda = getNumber(
				plan.precio_por_vianda,
				plan.precioPorVianda,
				plan.precio_unitario,
				plan.precio,
				0,
			);
			const cantidadDias = getNumber(plan.cantidad_dias, plan.cantidadDias, plan.dias, 0);

			return {
				...plan,
				nombre: plan.nombre || plan.titulo || `${cantidadDias || ''} días`.trim() || 'Plan de viandas',
				precioPorVianda,
				cantidadDias,
				descuento,
				descuentoId: plan.descuento_id || descuento?.id || null,
			};
		});
	}, [planes]);

	const getConfig = (planId) => opciones[planId] || { viandasDiarias: 1, cantidad: 1 };

	const getCouponHint = (plan) => {
		if (plan.descuentoId) {
			return VIANDA_COUPON_MESSAGES.discount;
		}

		if (plan.cantidadDias !== 20) {
			return `${VIANDA_COUPON_MESSAGES.general} Este plan tampoco admite cupones exclusivos para viandas de 20 días.`;
		}

		return `${VIANDA_COUPON_MESSAGES.general} Este plan sí admite cupones exclusivos para viandas de 20 días.`;
	};

	const setConfig = (planId, config) => {
		setOpciones((prev) => ({ ...prev, [planId]: { ...getConfig(planId), ...config } }));
	};

	const calcularPrecio = (plan, config) => {
		const base = plan.precioPorVianda * plan.cantidadDias * config.viandasDiarias;
		const descuento = plan.descuento;
		let unitarioFinal = base;

		if (descuento?.tipo === 'PORCENTAJE') {
			unitarioFinal = base * (1 - Number(descuento.valor || 0));
		} else if (descuento?.tipo === 'IMPORTE') {
			unitarioFinal = Math.max(base - Number(descuento.valor || 0), 0);
		}

		return unitarioFinal * config.cantidad;
	};

	const agregarPlan = async (plan) => {
		if (!puedeComprar) {
			toast.info('Iniciá sesión como cliente o administrador para agregar viandas al carrito.');
			return;
		}
		if (!token) {
			toast.error('Tu sesión no es válida. Volvé a iniciar sesión.');
			return;
		}
		const mensajeBloqueo = getMensajeBloqueoMezclaCarrito(cartItems, 'vianda');
		if (mensajeBloqueo) {
			toast.info(mensajeBloqueo);
			return;
		}

		const config = getConfig(plan.id);

		try {
			const { primerProductoAgregado } = await handleAddToCart(
				{
					...plan,
					titulo: plan.nombre,
					tipo: 'vianda',
					viandasDiarias: config.viandasDiarias,
					precio: calcularPrecio(plan, { ...config, cantidad: 1 }),
				},
				config.cantidad,
				null,
				token,
				userInfo.id,
				'vianda',
				dispatch,
				cantidadProductosCarrito,
			);
			toast.success('Plan de viandas agregado al carrito.');
			if (primerProductoAgregado) {
				toast.info('Tu carrito se guarda por 24 horas. Luego se limpiará automáticamente.');
			}
		} catch (error) {
			console.error(error);
			toast.error(error.message || 'No se pudo agregar la vianda al carrito.');
		}
	};

	const esCliente = userInfo?.rol === 'Cliente' || Number(userInfo?.rol_id) === 4;
	if (esCliente && viandasState.status === 'succeeded' && planesNormalizados.length === 0) {
		return <Navigate to="/" replace />;
	}

	return (
		<AnimationRevealPage>
			<Container>
				<ContentWithPaddingXl>
					<Section>
						<Heading>Nuestras viandas</Heading>
						<Muted>Elegí tu plan y definí si querés 1 o 2 viandas por día.</Muted>
					</Section>

					<Section>
						<FriendlyBox>
							El carrito de viandas es exclusivo: no se puede mezclar con productos o combos. Horario de
							entrega de viandas: 11:30 a 14:00.
						</FriendlyBox>
					</Section>

					<Section>
						{loading ?
							<Muted>Cargando planes de viandas...</Muted>
						: planesNormalizados.length === 0 ?
							<FriendlyBox>No hay planes activos por el momento.</FriendlyBox>
						:	<Grid>
								{planesNormalizados.map((plan) => {
									const config = getConfig(plan.id);
									const precioFinal = calcularPrecio(plan, config);
									const couponHint = getCouponHint(plan);
									const couponHintEsBloqueo = Boolean(plan.descuentoId);

									return (
										<Card key={plan.id}>
											<Title>{plan.nombre}</Title>
											<Meta>{plan.cantidadDias} días</Meta>
											<Meta>${plan.precioPorVianda.toFixed(2)} por vianda</Meta>

											<Controls>
												<span tw="text-sm font-semibold">Viandas diarias:</span>
												<ChoiceButton
													type="button"
													$active={config.viandasDiarias === 1}
													onClick={() => setConfig(plan.id, { viandasDiarias: 1 })}
												>
													1
												</ChoiceButton>
												<ChoiceButton
													type="button"
													$active={config.viandasDiarias === 2}
													onClick={() => setConfig(plan.id, { viandasDiarias: 2 })}
												>
													2
												</ChoiceButton>
											</Controls>

											<Controls>
												<span tw="text-sm font-semibold">Cantidad de packs:</span>
												<ControlButton
													type="button"
													onClick={() =>
														setConfig(plan.id, {
															cantidad: Math.max(1, config.cantidad - 1),
														})
													}
												>
													-
												</ControlButton>
												<span tw="font-semibold">{config.cantidad}</span>
												<ControlButton
													type="button"
													onClick={() =>
														setConfig(plan.id, { cantidad: config.cantidad + 1 })
													}
												>
													+
												</ControlButton>
											</Controls>

											<Price>${precioFinal.toFixed(2)}</Price>
											{plan.descuentoId && <Muted>Descuento aplicado a este plan.</Muted>}

											<AddButton type="button" onClick={() => agregarPlan(plan)}>
												Agregar al carrito
											</AddButton>
										</Card>
									);
								})}
							</Grid>
						}
					</Section>

					<Section>
						{menu?.imagen_url ?
							<div>
								<MenuImage src={menu.imagen_url} alt="Menu semanal de viandas" />
								<Muted tw="mt-3">
									{menu.semana_inicio ? `Semana desde ${menu.semana_inicio}` : 'Menú semanal actual'}
								</Muted>
							</div>
						:	<FriendlyBox>
								Todavía no hay menú semanal cargado. Estamos preparando las opciones de esta semana.
							</FriendlyBox>
						}
					</Section>
				</ContentWithPaddingXl>
			</Container>
		</AnimationRevealPage>
	);
}
